import { TimeSlot, TimeSlotAdditionalParams, TimeSlotOption } from '@checkout-v2/const';
import { MemoizedSelector, createSelector } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import { CustomCheckoutState, StateWithCustomCheckout } from '../checkout-state';
import { getCustomCheckoutState } from './feature.selector';

export const getTimeSlotsState: MemoizedSelector<
  StateWithCustomCheckout,
  StateUtils.LoaderState<TimeSlot[]>
> = createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => {
  return state.timeSlots;
});

export const getTimeSlotsForCustomDateState: MemoizedSelector<
  StateWithCustomCheckout,
  StateUtils.LoaderState<TimeSlot[]>
> = createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => {
  return state.timeSlotsForCustomDate;
});

export const getSelectedCustomDate: MemoizedSelector<StateWithCustomCheckout, string | undefined> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => {
    return state.customDate;
  },
);

export const getTimeSlots: MemoizedSelector<StateWithCustomCheckout, TimeSlot[]> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => {
    const timeSlots = StateUtils.loaderValueSelector(state.timeSlots) ?? [];
    const timeSlotsForCustomDate = StateUtils.loaderValueSelector(state.timeSlotsForCustomDate) ?? [];

    return timeSlots.concat(timeSlotsForCustomDate);
  },
);

export const getTimeSlotsLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderLoadingSelector(state),
);

export const getTimeSlotsSuccess: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderSuccessSelector(state),
);

export const getTimeSlotsError: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderErrorSelector(state),
);

export const getTimeSlotsForCustomDateLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsForCustomDateState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderLoadingSelector(state),
);

export const getTimeSlotsForCustomDateSuccess: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsForCustomDateState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderSuccessSelector(state),
);

export const getTimeSlotsForCustomDateError: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsForCustomDateState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderErrorSelector(state),
);

export const getTimeSlotsForCustomDateValue: MemoizedSelector<StateWithCustomCheckout, TimeSlot[]> = createSelector(
  getTimeSlotsForCustomDateState,
  (state: StateUtils.LoaderState<TimeSlot[]>) => StateUtils.loaderValueSelector(state),
);

export const getSelectedTimeSlotId: MemoizedSelector<StateWithCustomCheckout, string | undefined> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state.activeModes.timeSlotId,
);

export const getSelectedTimeSlot: MemoizedSelector<StateWithCustomCheckout, TimeSlotOption | undefined> =
  createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => {
    const selectedTimeSlotId = state.activeModes.timeSlotId;
    const timeSlots = state.timeSlots.value ?? [];
    const timeSlotsForCustomDate = state.timeSlotsForCustomDate.value ?? [];
    const combinedTimeSlots = timeSlots.concat(timeSlotsForCustomDate);

    if (!selectedTimeSlotId || !combinedTimeSlots.length) {
      return undefined;
    }

    const optionsArr = combinedTimeSlots.reduce<TimeSlotOption[]>((timeSlotAcc, timeSlot) => {
      return timeSlotAcc.concat(
        timeSlot.groups.reduce<TimeSlotOption[]>((groupAcc, group) => {
          return groupAcc.concat(group.options);
        }, []),
      );
    }, []);

    const foundTimeSlot = optionsArr.find((option) => option.id === selectedTimeSlotId);

    return foundTimeSlot;
  });

export const getSelectedTimeSlotAdditionalParams: MemoizedSelector<
  StateWithCustomCheckout,
  TimeSlotAdditionalParams | undefined
> = createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => {
  return state.activeModes.timeSlotAdditionalParams;
});
